'use client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PhoneInputProps, PhoneInputRefType, CountryData } from 'react-international-phone';
import type { InputHTMLAttributes, Ref } from 'react';
import { PhoneInput, defaultCountries } from 'react-international-phone';
import './phone-input-styles.css';

type PhoneInputCountry = Parameters<NonNullable<PhoneInputProps['onChange']>>[1]['country'];

// Countries whose national numbers start with a trunk "0" that is dropped after the dial code.
const TRUNK_PREFIX_COUNTRIES = new Set(['sk', 'cz']);

/**
 * Customers type their number the local way ("0949 123 456"). With `forceDialCode` the "+421"
 * prefix stays in place, so that becomes "+421 0949…" — drop the SK/CZ trunk 0 after the dial code
 * so the stored number is valid international format.
 */
function normalizePhone(phone: string, country?: PhoneInputCountry) {
  const digits = phone.replace(/\D/g, '');
  const dialCode = country?.dialCode?.replace(/\D/g, '');
  if (!dialCode || !TRUNK_PREFIX_COUNTRIES.has(country?.iso2?.toLowerCase() ?? '')) return phone;

  if (digits.startsWith(`${dialCode}0`)) {
    return `+${dialCode}${digits.slice(dialCode.length + 1)}`;
  }
  return phone;
}

export const PhoneInputField = forwardRef<
  HTMLInputElement,
  PhoneInputProps & { variant?: 'outlined' | 'default' } & Pick<
    InputHTMLAttributes<HTMLInputElement>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid'
  >
>(function PhoneInputField(
  {
    className,
    variant,
    onChange,
    inputProps,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...props
  },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const phoneInputRef = useRef<HTMLDivElement>(null);

  const customCountries = defaultCountries.map(country => {
    if (country[1] === 'sk') {
      return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
    }
    return country;
  });

  const locale = useLocaleStore(s => s.currentLocale);

  const defaultCountryFromLocale = () => {
    if (locale === 'cs') {
      return 'cz';
    }

    return locale;
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const current = phoneInputRef.current as any;

      if (current && !current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [phoneInputRef]);

  return (
    <div
      ref={phoneInputRef as any}
      className={cn('h-[52px] w-full overflow-hidden rounded-[4px] bg-[#F3F4F6]', className)}
    >
      <PhoneInput
        ref={ref as Ref<PhoneInputRefType>}
        inputProps={{
          ...inputProps,
          id: id ?? inputProps?.id,
          'aria-label': ariaLabel ?? inputProps?.['aria-label'],
          'aria-labelledby': ariaLabelledBy ?? inputProps?.['aria-labelledby'],
          'aria-describedby': ariaDescribedBy ?? inputProps?.['aria-describedby'],
          'aria-invalid': ariaInvalid ?? inputProps?.['aria-invalid'],
        }}
        defaultCountry={defaultCountryFromLocale()}
        name="phone"
        countries={customCountries}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        // The prefix used to be deletable: customers erased "+421", typed "0949…", and the field
        // stored "+0949…" — no dial code, 11 characters — which the register form rejected as
        // "Telefónne číslo je povinné". Keeping the dial code fixed makes that impossible.
        forceDialCode
        onChange={(phone, meta) => onChange?.(normalizePhone(phone, meta.country), meta)}
        inputClassName={cn(props.inputClassName)}
        inputStyle={{
          height: '48px',
          paddingLeft: '20px',
          width: '100%',
          border: 'none',
          borderEndEndRadius: 0,
          borderStartEndRadius: 0,
          backgroundColor: 'transparent',
        }}
        countrySelectorStyleProps={{
          buttonClassName: '',
          buttonStyle: {
            height: '48px',
            border: 'none',
            borderStartStartRadius: 0,
            borderEndStartRadius: 0,
            paddingLeft: '12px',
            backgroundColor: 'transparent',
          },
        }}
        {...props}
      />
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
